/**
 * The one error shape every API call can throw.
 *
 * `status` is the HTTP status (0 when the request never reached the server),
 * `code` is the backend's `detail` string or a synthetic one (`network_error`,
 * `not_authenticated`), and `message` is safe to put in front of a person.
 * Mirrors the Flutter client's ApiException so both apps read failures the
 * same way.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** Needs the plan (HTTP 402): the free minutes are spent, or this is a paid feature. */
  get needsPlan(): boolean {
    return this.status === 402;
  }

  /** The thing does not exist yet — used to tell "no profile" from a failure. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The token was rejected or has expired. */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /**
   * The server could not *verify* the token, as opposed to rejecting it. The
   * session may be perfectly valid, so callers retry rather than sign out.
   */
  get isVerificationUnavailable(): boolean {
    return this.status === 503 || this.code === "verification_unavailable";
  }

  static network(): ApiError {
    return new ApiError(
      0,
      "network_error",
      "We couldn't reach the server. Check your connection and try again.",
    );
  }

  static notAuthenticated(): ApiError {
    return new ApiError(401, "not_authenticated", "Please sign in to continue.");
  }

  static badResponse(code = "bad_response"): ApiError {
    return new ApiError(502, code, "The server returned an unexpected response.");
  }

  /** Builds the error for a non-2xx response from its status and detail code. */
  static fromStatus(status: number, code: string): ApiError {
    return new ApiError(status, code, friendlyMessage(status, code));
  }
}

/**
 * A sentence a person can act on, for each failure the backend produces.
 * Kept in step with the Flutter client so the two apps never disagree.
 */
export function friendlyMessage(status: number, code: string): string {
  if (code === "no_time_left") {
    return "Your free minutes are used. Subscribe to keep talking with Antar.";
  }
  if (status === 402) return "This is part of the Antar plan.";
  if (status === 401 || status === 403) {
    return "Your session has expired. Please sign in again.";
  }
  if (code === "reading_unavailable") {
    return "She couldn’t answer just now. Nothing was counted — try again in a moment.";
  }
  if (code === "username_taken") return "That handle is taken. Try another.";
  if (code === "username_required") {
    return "Claim a handle first. A page with no address is not a page.";
  }
  if (status === 409) return "That's already taken. Try another.";
  if (status === 429) {
    return "That's a lot of requests at once. Try again in a little while.";
  }
  if (code === "contact_matching_unavailable") {
    return "Contact matching is unavailable right now. Try again later.";
  }
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 422) return "Some details look off. Please review and try again.";
  if (status >= 500) return "Something went wrong on our end. Please try again shortly.";
  return "Something went wrong. Please try again.";
}

/** Narrows any thrown value to a message a person can read. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
